import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CandidateLayout from '../../layouts/CandidateLayout';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { getExam, startSession } from '../../api/candidate.api';
import useFullscreen from '../../hooks/useFullscreen';
import { useDispatch } from 'react-redux';
import { setSessionId, setCurrentExam } from '../../store/examSlice';

const InfoTile = ({ value, label }) => (
  <div className="bg-slate-50 rounded-2xl p-4 text-center border border-slate-200">
    <p className="text-2xl font-bold text-primary-700">{value}</p>
    <p className="text-xs text-slate-500 mt-1 font-medium">{label}</p>
  </div>
);

const RuleItem = ({ type, text }) => (
  <li className="flex items-start gap-3">
    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
      type === 'warn' ? 'bg-red-100' : 'bg-emerald-100'
    }`}>
      {type === 'warn' ? (
        <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className="w-3 h-3 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </span>
    <span className="text-sm text-slate-700">{text}</span>
  </li>
);

const formatCountdown = (ms) => {
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
};

const useSchedule = (exam) => {
  const [scheduleState, setScheduleState] = useState({ status: 'open', countdown: '' });

  useEffect(() => {
    if (!exam) return;
    const tick = () => {
      const now = Date.now();
      const start = exam.scheduled_start ? new Date(exam.scheduled_start).getTime() : null;
      const end = exam.scheduled_end ? new Date(exam.scheduled_end).getTime() : null;

      if (start && now < start) {
        setScheduleState({ status: 'pending', countdown: formatCountdown(start - now) });
      } else if (end && now > end) {
        setScheduleState({ status: 'ended', countdown: '' });
      } else if (start || end) {
        const remaining = end ? formatCountdown(end - now) : '';
        setScheduleState({ status: 'live', countdown: remaining });
      } else {
        setScheduleState({ status: 'open', countdown: '' });
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [exam]);

  return scheduleState;
};

const ExamLobby = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enter: enterFullscreen } = useFullscreen();
  const [exam, setExam] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { status: scheduleStatus, countdown } = useSchedule(exam);

  useEffect(() => {
    getExam(id).then((res) => setExam(res.data.data)).catch(() => setError('Exam not found'));
  }, [id]);

  const handleStart = async () => {
    if (!agreed) { setError('Please accept the exam rules to proceed.'); return; }
    setLoading(true);
    setError('');
    try {
      await enterFullscreen().catch(() => {});
      const res = await startSession(Number(id), exam.has_access_code ? accessCode : null);
      const sessionId = res.data.session.id;
      dispatch(setCurrentExam(exam));
      dispatch(setSessionId(sessionId));
      navigate(`/candidate/exams/${id}/take?session=${sessionId}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start exam');
    } finally {
      setLoading(false);
    }
  };

  if (!exam) return <CandidateLayout><Spinner className="py-20" /></CandidateLayout>;

  return (
    <CandidateLayout>
      <div className="max-w-2xl mx-auto">
        {/* Card */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-card-lg overflow-hidden">
          {/* Header banner */}
          <div className="bg-primary-gradient px-8 py-7 text-white">
            <div className="inline-flex items-center gap-1.5 bg-white/20 px-3 py-1 rounded-full text-xs font-semibold mb-3">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Assessment
            </div>
            <h1 className="text-2xl font-bold leading-tight">{exam.title}</h1>
            {exam.description && (
              <p className="text-white/70 mt-1.5 text-sm">{exam.description}</p>
            )}
          </div>

          {/* Schedule banner */}
          {scheduleStatus === 'pending' && (
            <div className="mx-8 mt-5 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-800">Exam not started yet</p>
                <p className="text-xs text-amber-700">Starts in <span className="font-mono font-bold">{countdown}</span></p>
              </div>
            </div>
          )}
          {scheduleStatus === 'live' && countdown && (
            <div className="mx-8 mt-5 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3">
              <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-emerald-800">Exam is live</p>
                <p className="text-xs text-emerald-700">Closes in <span className="font-mono font-bold">{countdown}</span></p>
              </div>
            </div>
          )}
          {scheduleStatus === 'ended' && (
            <div className="mx-8 mt-5 flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-5 py-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-red-800">This exam has ended and is no longer available.</p>
            </div>
          )}

          {/* Info tiles */}
          <div className="px-8 py-5 border-b border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <InfoTile value={`${exam.duration_minutes}`} label="Minutes" />
            <InfoTile value={exam.question_count} label="Questions" />
            <InfoTile value={exam.total_marks} label="Total Marks" />
            <InfoTile value={exam.pass_marks} label="Pass Marks" />
          </div>

          {/* Instructions + rules */}
          <div className="px-8 py-6 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide mb-4">
              Rules &amp; Instructions
            </h2>
            {exam.instructions && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl px-5 py-4 mb-4">
                <pre className="whitespace-pre-wrap text-sm text-blue-900 font-sans">{exam.instructions}</pre>
              </div>
            )}
            <ul className="space-y-2.5">
              <RuleItem type="warn" text={`Auto-submit after ${exam.max_tab_switches} tab switch(es) — stay focused.`} />
              <RuleItem type="warn" text="Copy, paste, right-click, and text selection are disabled." />
              <RuleItem type="warn" text="Must be taken in fullscreen. Exiting fullscreen will be flagged." />
              <RuleItem type="warn" text="Timer runs even if you refresh. Exam auto-submits when time expires." />
              <RuleItem type="ok" text="Answers are auto-saved as you go — no need to manually save." />
              <RuleItem type="ok" text="You can navigate between questions at any time before submitting." />
              {exam.negative_marks_per_wrong > 0 && (
                <RuleItem type="warn" text={`Negative marking is enabled: ${exam.negative_marks_per_wrong} mark(s) deducted per wrong answer. Unanswered questions carry no penalty.`} />
              )}
            </ul>
          </div>

          {/* Agreement + CTA */}
          <div className="px-8 py-6">
            {exam.has_access_code && (
              <div className="mb-5">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Access Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter exam access code"
                  className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer group mb-5">
              <div className={`relative w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                agreed ? 'bg-primary-600 border-primary-600' : 'border-slate-300 group-hover:border-primary-400'
              }`}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
                {agreed && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-sm text-slate-700 leading-relaxed">
                I have read and agree to all exam rules. I understand that any violation may result in automatic submission or disqualification.
              </span>
            </label>

            {error && <Alert type="error" className="mb-4">{error}</Alert>}

            <Button
              onClick={handleStart}
              loading={loading}
              disabled={!agreed || scheduleStatus === 'pending' || scheduleStatus === 'ended' || (exam.has_access_code && !accessCode.trim())}
              size="lg"
              fullWidth
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
              </svg>
              Enter Fullscreen &amp; Start Exam
            </Button>
          </div>
        </div>
      </div>
    </CandidateLayout>
  );
};

export default ExamLobby;
